import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import EnumInput from '../../../../../misc/EnumInput';
import NullableInput from '../../../../../misc/NullableInput';
import { CoreSchoolSoftConfiguration, SchoolSoftActivityNaming, SchoolSoftImportType, SchoolSoftSensitiveFields, SchoolSoftTargetSystem } from '../../../../../../models/CoreSchoolSoftConfigurationModels';
import * as SchoolSoftConfigurationTypes from './SchoolSoftConfigurationTypes';
import i18n from 'i18next';


export const validator = (configuration: CoreSchoolSoftConfiguration, active: boolean) => {
    const validation = [];

    if (active) {
        if (configuration.type === SchoolSoftImportType.Unknown) {
            validation.push('type');
        }
        if (configuration.targetSystem === SchoolSoftTargetSystem.Unknown) {
            validation.push('targetSystem');
        }
        if ((configuration.type === SchoolSoftImportType.Full || configuration.type === SchoolSoftImportType.HybridSS12000Activities) && configuration.activityNaming === SchoolSoftActivityNaming.Undefined) {
            validation.push('activityNaming');
        }
        if (!configuration.webApp || configuration.webApp === "") {
            validation.push('webApp');
        }
    }
    if (configuration.municipalityCode === '') {
        validation.push("municipalityCode");
    }
    return validation;
}

const hasDeselectedType = (originalValue: SchoolSoftSensitiveFields, updatedValue: undefined | Partial<SchoolSoftSensitiveFields>, type: SchoolSoftSensitiveFields) => {
    return updatedValue !== undefined && (originalValue & type) === type && (updatedValue & type) === 0;
}

export const warningMessages = (originalConfiguration: CoreSchoolSoftConfiguration, updatedConfiguration: Partial<CoreSchoolSoftConfiguration>) => {
    const warnings: string[] = [];

    if (hasDeselectedType(originalConfiguration.sensitiveFields, updatedConfiguration.sensitiveFields, SchoolSoftSensitiveFields.Custodians)) {
        warnings.push(i18n.t('schoolSoftConfiguration.warnings.deselectedCustodian', { ns: 'configuration' }));
    }

    return warnings;
}

interface SchoolSoftConfigurationProps {
    configuration: CoreSchoolSoftConfiguration
    disabled: boolean
    updatePart: (part: Partial<CoreSchoolSoftConfiguration>) => void
    active: boolean
    onValidChange: (arg: boolean) => void 
}


const SchoolSoftConfiguration = (props: SchoolSoftConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);
    const [open, setOpen] = useState<boolean>(true);

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.SchoolSoft.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>

                        <EnumInput
                            title={t('schoolSoftConfiguration.typeOfImport.title', { ns: 'configuration' })}
                            value={props.configuration.type}
                            options={SchoolSoftConfigurationTypes.importType.map(x => ({ value: x, text: t(`schoolSoftConfiguration.typeOfImport.options.${x}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            name='type'
                            invalid={validation.indexOf('type') !== -1}
                            invalidFeedback={t('schoolSoftConfiguration.typeOfImport.invalidFeedback', { ns: 'configuration' })}
                        />

                        {(props.configuration.type === SchoolSoftImportType.Full || props.configuration.type === SchoolSoftImportType.HybridSS12000Activities) &&
                            <EnumInput
                                title={t('schoolSoftConfiguration.activitiesNaming.title', { ns: 'configuration' })}
                                value={props.configuration.activityNaming}
                                options={SchoolSoftConfigurationTypes.activityNaming.map(x => ({ value: x, text: t(`schoolSoftConfiguration.activitiesNaming.options.${x}`, { ns: 'configuration' }) }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled || (props.configuration.type !== 1 && props.configuration.type !== 4)}
                                name='activityNaming'
                                invalid={validation.indexOf('activityNaming') !== -1}
                                invalidFeedback={t('schoolSoftConfiguration.activitiesNaming.invalidFeedback', { ns: 'configuration' })}
                            />}

                        <EnumInput
                            title={t('schoolSoftConfiguration.importType.title', { ns: 'configuration' })}
                            name='targetSystem'
                            value={props.configuration.targetSystem}
                            options={SchoolSoftConfigurationTypes.targetSystem.map(x => ({ value: x, text: t(`schoolSoftConfiguration.importType.options.${x}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            invalid={validation.indexOf('targetSystem') !== -1}
                            invalidFeedback={t('schoolSoftConfiguration.importType.invalidFeedback', { ns: 'configuration' })}
                        />

                        <EnumInput
                            multi={true}
                            title={t('schoolSoftConfiguration.dataProcessing.title', { ns: 'configuration' })}
                            name="sensitiveFields"
                            value={props.configuration.sensitiveFields}
                            options={SchoolSoftConfigurationTypes.sensitiveField.map(x => ({ value: x, text: t(`schoolSoftConfiguration.dataProcessing.options.${x}`, { ns: 'configuration' }) }))}
                            disabled={props.disabled}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                        />

                        {(props.configuration.type === SchoolSoftImportType.Full || props.configuration.type === SchoolSoftImportType.Hybrid || props.configuration.type === SchoolSoftImportType.HybridSS12000Activities) &&
                            <NullableInput
                                disabled={props.disabled}
                                title={t('schoolSoftConfiguration.municipalityCodeFiltering.title', { ns: 'configuration' })}
                                checkboxTitle={t('schoolSoftConfiguration.municipalityCodeFiltering.checkboxTitle', { ns: 'configuration' })}
                                value={props.configuration.municipalityCode}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                name="municipalityCode"
                                invalid={validation.indexOf('municipalityCode') !== -1}
                                invalidFeedback={t('schoolSoftConfiguration.municipalityCodeFiltering.invalidFeedback', { ns: 'configuration' })}
                            />}

                        <FormGroup>
                            <Label tag="h5" for="baseAddress">{t('schoolSoftConfiguration.webapp.label', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="webApp"
                                id="webApp"
                                placeholder={t('schoolSoftConfiguration.webapp.placeholder', { ns: 'configuration' })}
                                value={props.configuration.webApp || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ webApp: e.target.value })}
                                invalid={validation.indexOf('webApp') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback>{t('schoolSoftConfiguration.webapp.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>
                    </div>
                </CardBody>
            </Collapse>
        </Card>
    )
}

export default SchoolSoftConfiguration;
